import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";

const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

export default function EstimateForm({ oppId, kind, onDone, submitUrl, extraBody, title = "Send your estimate" }) {
  const [f, setF] = useState({ price: "", pricing_type: "fixed", intro: "", scope: "", estimated_hours: "", start_date: "", delivery_date: "", inclusions: "", exclusions: "", valid_days: 14, terms: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const local = kind === "local";

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(submitUrl || `/service-requests/opportunities/${oppId}/estimate`, {
        ...extraBody,
        price: Number(f.price), pricing_type: f.pricing_type, intro: f.intro, scope: f.scope,
        estimated_hours: f.estimated_hours ? Number(f.estimated_hours) : null,
        start_date: f.start_date ? new Date(f.start_date).toISOString() : null,
        delivery_date: f.delivery_date ? new Date(f.delivery_date).toISOString() : null,
        inclusions: lines(f.inclusions), exclusions: lines(f.exclusions), valid_days: Number(f.valid_days) || 14, terms: f.terms || null,
      });
      toast.success("Estimate sent to the customer");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not send estimate");
    } finally {
      setBusy(false);
    }
  };
  const ok = Number(f.price) > 0 && f.intro.trim() && f.scope.trim() && (!local || f.start_date);

  return (
    <div className="rounded-3xl border border-[#165a71]/30 bg-white p-6 space-y-4" data-testid="estimate-form">
      <div className="font-display text-lg font-semibold text-[#071b22]">{title}</div>
      <div className="grid gap-3 grid-cols-[1fr_auto]">
        <label><div className="text-xs mb-1 text-[#252A34]/70">Price ($) *</div><input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className="sr-input" data-testid="est-price" /></label>
        <label><div className="text-xs mb-1 text-[#252A34]/70">Type</div>
          <select value={f.pricing_type} onChange={(e) => set("pricing_type", e.target.value)} className="sr-input" data-testid="est-pricing-type"><option value="fixed">Fixed total</option><option value="hourly">Hourly</option></select>
        </label>
      </div>
      <label className="block"><div className="text-xs mb-1 text-[#252A34]/70">Short intro *</div><input value={f.intro} onChange={(e) => set("intro", e.target.value)} placeholder="Why you're a great fit" className="sr-input" data-testid="est-intro" /></label>
      <label className="block"><div className="text-xs mb-1 text-[#252A34]/70">Scope of work *</div><textarea rows={4} value={f.scope} onChange={(e) => set("scope", e.target.value)} className="sr-input" data-testid="est-scope" /></label>
      <div className="grid gap-3 sm:grid-cols-3">
        <label><div className="text-xs mb-1 text-[#252A34]/70">{local ? "Proposed start *" : "Start date"}</div><input type="datetime-local" value={f.start_date} onChange={(e) => set("start_date", e.target.value)} className="sr-input" data-testid="est-start" /></label>
        <label><div className="text-xs mb-1 text-[#252A34]/70">{local ? "Est. hours" : "Delivery date"}</div>
          {local ? <input type="number" value={f.estimated_hours} onChange={(e) => set("estimated_hours", e.target.value)} className="sr-input" data-testid="est-hours" />
            : <input type="date" value={f.delivery_date} onChange={(e) => set("delivery_date", e.target.value)} className="sr-input" data-testid="est-delivery" />}
        </label>
        <label><div className="text-xs mb-1 text-[#252A34]/70">Valid for (days)</div><input type="number" value={f.valid_days} onChange={(e) => set("valid_days", e.target.value)} className="sr-input" data-testid="est-valid" /></label>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label><div className="text-xs mb-1 text-[#252A34]/70">Included (one per line)</div><textarea rows={3} value={f.inclusions} onChange={(e) => set("inclusions", e.target.value)} className="sr-input" data-testid="est-inclusions" /></label>
        <label><div className="text-xs mb-1 text-[#252A34]/70">Not included (one per line)</div><textarea rows={3} value={f.exclusions} onChange={(e) => set("exclusions", e.target.value)} className="sr-input" data-testid="est-exclusions" /></label>
      </div>
      <button onClick={submit} disabled={!ok || busy} className="w-full rounded-full bg-[#165a71] text-white py-3 text-sm font-semibold hover:bg-[#11485a] disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="est-submit">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send estimate
      </button>
    </div>
  );
}
